import {
  FolderOpenIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
  UserMinusIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { AvatarIniciales } from '@/components/marca/Avatar'
import { Boton } from '@/components/ui/Boton'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { cn } from '@/lib/cn'
import type {
  AccesoAdmin,
  DetalleProyectoAdmin,
  PilarAdmin,
  ProyectoAdmin,
} from '@/lib/schemas/admin'
import { useDarDeBajaPilar, useQuitarAcceso, useReenviarInvitacion } from './api'
import { Confirmacion } from './Confirmacion'
import { formatearDiaCorto, formatearPeriodo } from './formato'
import { ModalInvitar } from './ModalInvitar'
import { ModalPilar } from './ModalPilar'
import { Panel } from './Panel'

interface DetalleProyectoProps {
  detalle: DetalleProyectoAdmin | null
  cargando: boolean
  /** Proyectos activos del cliente, para las casillas de Invitar persona. */
  proyectosDelCliente: ProyectoAdmin[]
  busqueda: string
  abrirNuevoPilar: boolean
  alCerrarNuevoPilar: () => void
  abrirInvitar: boolean
  alCerrarInvitar: () => void
}

/** P03 · Detalle del proyecto: Resumen, Pilares y Personas con acceso (§8.6). */
export function DetalleProyecto({
  detalle,
  cargando,
  proyectosDelCliente,
  busqueda,
  abrirNuevoPilar,
  alCerrarNuevoPilar,
  abrirInvitar,
  alCerrarInvitar,
}: DetalleProyectoProps) {
  const [pilarEditando, setPilarEditando] = useState<PilarAdmin | null>(null)
  const [nuevoPilarLocal, setNuevoPilarLocal] = useState(false)
  const [pilarABajar, setPilarABajar] = useState<PilarAdmin | null>(null)
  const [invitarLocal, setInvitarLocal] = useState(false)
  const [accesoAQuitar, setAccesoAQuitar] = useState<AccesoAdmin | null>(null)
  const darDeBaja = useDarDeBajaPilar()
  const quitarAcceso = useQuitarAcceso()
  const reenviar = useReenviarInvitacion()
  const avisar = useAvisar()

  if (!detalle) {
    return (
      <section className="flex min-h-40 items-center justify-center rounded-tarjeta bg-tarjeta p-8 text-center text-sm text-tinta-suave shadow-tarjeta">
        {cargando ? 'Cargando…' : 'Elegí un proyecto para ver su detalle.'}
      </section>
    )
  }

  const { proyecto, cliente, pilares, personas } = detalle
  const filtro = busqueda.trim().toLowerCase()
  const personasVisibles = personas.filter(
    (p) =>
      !filtro ||
      p.nombre.toLowerCase().includes(filtro) ||
      p.email.toLowerCase().includes(filtro),
  )

  const modalPilarAbierto = abrirNuevoPilar || nuevoPilarLocal || pilarEditando !== null
  const cerrarModalPilar = () => {
    setNuevoPilarLocal(false)
    setPilarEditando(null)
    alCerrarNuevoPilar()
  }
  const modalInvitarAbierto = abrirInvitar || invitarLocal
  const cerrarModalInvitar = () => {
    setInvitarLocal(false)
    alCerrarInvitar()
  }

  return (
    <div className="flex flex-col gap-4" data-detalle-proyecto>
      {/* Resumen — sin contadores (diferencia 6 del §11). */}
      <Panel titulo="Resumen">
        <div className="flex flex-col gap-4 px-4 pb-4">
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-borde text-sm font-semibold">
              {cliente.nombre.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {cliente.nombre} · {proyecto.nombre}
              </p>
              <p className="text-xs text-tinta-suave">
                {proyecto.activo
                  ? formatearPeriodo(proyecto.periodoDesde, proyecto.periodoHasta)
                  : 'Proyecto inactivo'}
              </p>
            </div>
          </div>
          {proyecto.activo && (
            <Boton asChild className="w-full">
              <Link to={`/proyecto/${proyecto.id}`}>
                <FolderOpenIcon className="size-4" aria-hidden />
                Abrir proyecto
              </Link>
            </Boton>
          )}
        </div>
      </Panel>

      <Panel
        titulo="Pilares"
        accion={
          <Boton
            tamano="chico"
            onClick={() => {
              setNuevoPilarLocal(true)
            }}
          >
            <PlusIcon className="size-4" aria-hidden />
            Nuevo pilar
          </Boton>
        }
      >
        {pilares.length === 0 ? (
          <p className="px-4 py-6 text-sm text-tinta-suave">
            Este proyecto no tiene pilares todavía.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5 px-3 pb-3" aria-label="Pilares">
            {pilares.map((p) => (
              <li
                key={p.id}
                data-pilar={p.nombre}
                className="flex min-h-11 items-center gap-3 rounded-full bg-lienzo py-1 pr-1 pl-4"
              >
                <span
                  className="size-3 shrink-0 rounded-full"
                  style={{ backgroundColor: `var(--color-pilar-${String(p.color)})` }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-sm">{p.nombre}</span>
                {p.descripcion && (
                  <span className="hidden max-w-64 truncate text-xs text-tinta-suave xl:inline">
                    {p.descripcion}
                  </span>
                )}
                <MenuDesplegable>
                  <MenuDisparador asChild>
                    <Boton
                      variante="sutil"
                      tamano="icono"
                      aria-label={`Opciones de ${p.nombre}`}
                    >
                      <MoreVerticalIcon className="size-5" aria-hidden />
                    </Boton>
                  </MenuDisparador>
                  <MenuContenido>
                    <MenuItem
                      onSelect={() => {
                        setPilarEditando(p)
                      }}
                    >
                      <PencilIcon className="size-4" aria-hidden />
                      Editar
                    </MenuItem>
                    <MenuItem
                      peligro
                      disabled={p.publicaciones > 0}
                      {...(p.publicaciones > 0
                        ? { detalle: 'Tiene publicaciones asociadas' }
                        : {})}
                      onSelect={() => {
                        setPilarABajar(p)
                      }}
                    >
                      <Trash2Icon className="size-4" aria-hidden />
                      Dar de baja
                    </MenuItem>
                  </MenuContenido>
                </MenuDesplegable>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        titulo="Personas con acceso"
        accion={
          <Boton
            tamano="chico"
            disabled={!proyecto.activo}
            onClick={() => {
              setInvitarLocal(true)
            }}
          >
            <PlusIcon className="size-4" aria-hidden />
            Invitar persona
          </Boton>
        }
      >
        {personas.length === 0 ? (
          <p className="px-4 py-6 text-sm text-tinta-suave">
            Todavía nadie del cliente tiene acceso a este proyecto.
          </p>
        ) : personasVisibles.length === 0 ? (
          <p className="px-4 py-6 text-sm text-tinta-suave">
            Ninguna persona coincide con la búsqueda.
          </p>
        ) : (
          <ul
            className="divide-y divide-borde px-4 pb-2"
            aria-label="Personas con acceso"
          >
            {personasVisibles.map((a) => (
              <li
                key={a.usuarioId}
                data-persona={a.email}
                className="flex items-center gap-3 py-2.5"
              >
                <AvatarIniciales nombre={a.nombre} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{a.nombre}</p>
                  <p className="truncate text-xs text-tinta-suave">{a.email}</p>
                  {a.otrosProyectos.length > 0 && (
                    <p
                      data-otros-proyectos
                      className="truncate text-xs text-tinta-suave"
                      title={a.otrosProyectos.join(', ')}
                    >
                      También en {a.otrosProyectos.join(', ')}
                    </p>
                  )}
                </div>
                <span
                  data-acceso={a.estado}
                  className={cn(
                    'rounded-full px-3 py-1 text-[13px] font-medium',
                    a.estado === 'activa'
                      ? 'bg-aprobada text-aprobada-tinta'
                      : 'bg-cambios text-cambios-tinta',
                  )}
                >
                  {a.estado === 'activa' ? 'Activa' : 'Invitada'}
                </span>
                <span className="hidden w-28 text-right text-xs text-tinta-suave sm:block">
                  {a.estado === 'activa' ? 'desde el' : 'invitada el'}{' '}
                  {formatearDiaCorto(a.fecha)}
                </span>
                <MenuDesplegable>
                  <MenuDisparador asChild>
                    <Boton
                      variante="sutil"
                      tamano="icono"
                      aria-label={`Opciones de ${a.nombre}`}
                    >
                      <MoreVerticalIcon className="size-5" aria-hidden />
                    </Boton>
                  </MenuDisparador>
                  <MenuContenido>
                    <MenuItem
                      detalle={`Desde la casilla de ${cliente.remitente}`}
                      onSelect={() => {
                        reenviar.mutate(
                          { usuarioId: a.usuarioId, proyectoId: proyecto.id },
                          {
                            onSuccess: (r) => {
                              avisar(
                                r.mail === 'enviado'
                                  ? `Le reenviamos la invitación a ${a.nombre}`
                                  : `No se pudo mandar el mail a ${a.nombre}. Probá de nuevo más tarde.`,
                              )
                            },
                            onError: () => {
                              avisar(`No se pudo mandar el mail a ${a.nombre}.`)
                            },
                          },
                        )
                      }}
                    >
                      <SendIcon className="size-4" aria-hidden />
                      Reenviar invitación
                    </MenuItem>
                    <MenuItem
                      peligro
                      onSelect={() => {
                        setAccesoAQuitar(a)
                      }}
                    >
                      <UserMinusIcon className="size-4" aria-hidden />
                      Quitar acceso
                    </MenuItem>
                  </MenuContenido>
                </MenuDesplegable>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <ModalPilar
        abierto={modalPilarAbierto}
        alCerrar={cerrarModalPilar}
        proyectoId={proyecto.id}
        pilar={pilarEditando}
      />

      <ModalInvitar
        abierto={modalInvitarAbierto}
        alCerrar={cerrarModalInvitar}
        clienteId={cliente.id}
        clienteNombre={cliente.nombre}
        remitente={cliente.remitente}
        proyectos={proyectosDelCliente}
        proyectoActualId={proyecto.id}
      />

      <Confirmacion
        abierto={pilarABajar !== null}
        alCerrar={() => {
          setPilarABajar(null)
        }}
        titulo="Dar de baja el pilar"
        texto={`${pilarABajar?.nombre ?? ''} deja de ofrecerse para las publicaciones nuevas.`}
        confirmar="Dar de baja"
        peligro
        alConfirmar={async () => {
          if (!pilarABajar) return
          await darDeBaja.mutateAsync({ id: pilarABajar.id, proyectoId: proyecto.id })
          avisar('Pilar dado de baja')
        }}
      />

      <Confirmacion
        abierto={accesoAQuitar !== null}
        alCerrar={() => {
          setAccesoAQuitar(null)
        }}
        titulo="Quitar acceso"
        texto={textoQuitarAcceso(accesoAQuitar)}
        confirmar="Quitar acceso"
        peligro
        alConfirmar={async () => {
          if (!accesoAQuitar) return
          await quitarAcceso.mutateAsync({
            usuarioId: accesoAQuitar.usuarioId,
            proyectoId: proyecto.id,
          })
          avisar('Acceso quitado')
        }}
      />
    </div>
  )
}

/** Nombra los proyectos que la persona sigue viendo: quitar uno no quita los demás. */
function textoQuitarAcceso(a: AccesoAdmin | null): string {
  if (!a) return ''
  const base = `${a.nombre} va a dejar de ver este proyecto de inmediato. Sus comentarios y aprobaciones se conservan.`
  const otros = a.otrosProyectos
  if (otros.length === 0) return base
  const lista =
    otros.length === 1
      ? `«${otros[0] ?? ''}»`
      : `${otros
          .slice(0, -1)
          .map((o) => `«${o}»`)
          .join(', ')} y «${otros.at(-1) ?? ''}»`
  return `${base} Sigue viendo ${lista}, del mismo cliente: si tampoco tiene que verlo, quitale el acceso desde ese proyecto.`
}
